import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { PageFade, Reveal } from "@/components/site/motion";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "";

export default function Blog() {
  useDocumentTitle("Journal — JARE");
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(posts.map((p) => p.category).filter(Boolean))], [posts]);
  const filtered = selected.length === 0 ? posts : posts.filter((p) => selected.includes(p.category));
  const [featured, ...rest] = filtered;

  return (
    <PageFade className="mx-auto w-[92%] max-w-7xl pt-40 pb-28">
      <p className="eyebrow mb-6">Journal</p>
      <h1 className="font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="blog-page-title">
        Notes on craft
      </h1>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
        Essays and field notes on design, motion and building a visual practice.
      </p>

      <div className="mt-12 flex flex-wrap items-center gap-2.5" data-testid="blog-filters">
        <button
          onClick={() => setSelected([])}
          data-testid="blog-filter-all"
          className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
            selected.length === 0
              ? "border-white bg-white text-black"
              : "border-white/12 bg-white/[0.03] text-neutral-400 hover:border-white/30 hover:text-white"
          }`}
        >
          All
        </button>
        {categories.map((c) => {
          const isActive = selected.includes(c);
          return (
            <button
              key={c}
              onClick={() => setSelected((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                isActive
                  ? "border-white bg-white text-black"
                  : "border-white/12 bg-white/[0.03] text-neutral-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {c}
            </button>
          );
        })}
        {selected.length > 0 && (
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500" data-testid="blog-filter-count">
            {selected.length} selected
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-14 aspect-[21/9] animate-pulse rounded-3xl bg-white/[0.04]" />
      ) : filtered.length === 0 ? (
        <p className="mt-20 text-center text-neutral-500" data-testid="blog-empty">
          No published posts here yet.
        </p>
      ) : (
        <>
          {featured && (
            <Reveal className="mt-12">
              <Link to={`/blog/${featured.slug}`} className="group block" data-testid={`blog-featured-${featured.slug}`}>
                <article className="glass glass-hover grid grid-cols-1 overflow-hidden rounded-3xl lg:grid-cols-2">
                  <div className="overflow-hidden">
                    <img
                      src={mediaUrl(featured.featured_image)}
                      alt={featured.title}
                      loading="lazy"
                      className="aspect-[16/10] h-full w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-8 md:p-12">
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                      <span className="rounded-full border border-white/15 px-3 py-1 text-white/80">{featured.category}</span>
                      <span>{fmtDate(featured.published_at)}</span>
                    </div>
                    <h2 className="font-display mt-5 text-2xl font-bold tracking-tight text-white md:text-3xl lg:text-4xl">
                      {featured.title}
                    </h2>
                    <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-neutral-400 md:text-base">
                      {featured.excerpt}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white">
                      Read article
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </article>
              </Link>
            </Reveal>
          )}

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8" data-testid="blog-grid">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.07}>
                <Link to={`/blog/${p.slug}`} className="group block h-full" data-testid={`blog-card-${p.slug}`}>
                  <article className="glass glass-hover flex h-full flex-col overflow-hidden rounded-3xl">
                    <div className="overflow-hidden">
                      <img
                        src={mediaUrl(p.featured_image)}
                        alt={p.title}
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        <span className="text-white/70">{p.category}</span>
                        <span>·</span>
                        <span>{fmtDate(p.published_at)}</span>
                      </div>
                      <h3 className="font-display mt-3 text-xl font-bold tracking-tight text-white">{p.title}</h3>
                      <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-neutral-400">{p.excerpt}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-xs text-neutral-500">
                        <Clock className="h-3.5 w-3.5" /> {p.reading_time} min read
                      </span>
                    </div>
                  </article>
                </Link>
              </Reveal>
            ))}
          </div>
        </>
      )}
    </PageFade>
  );
}
